// NewOrganisationWizard — "Ny organisation" (#358).
//
// Replaces "Kundetablering", which was confusing in three separate ways:
//
//   1. It was called after a thing ("kund") that is not an entity. It created an
//      organisation, a unit AND an employer, so "kund" meant all three at once.
//      The product now says organisation everywhere, for the thing the database
//      calls an organisation.
//   2. It asked for them in the wrong order — Enhet, then Användarkonto, then
//      Organisation — the reverse of how they nest. You were naming a department
//      before naming the employer it belongs to.
//   3. It did two unrelated jobs behind a tab strip: create a NEW organisation,
//      or attach a unit to an EXISTING one. The second job already lives on the
//      organisation's own page ("Lägg till Enhet"), which is where you are when
//      you want it. That tab is gone; this page only ever creates something new.
//
// So: three steps in the order the domain nests, and exactly three required
// fields across all of them — organisation name, unit name, employer email.
// Everything else is genuinely optional and stays collapsed until asked for,
// because a field you can see is a field you think you must fill in.
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Building, User, Check, ArrowLeft, ArrowRight, ChevronDown, Mail } from 'lucide-react';
import { useAdmin } from '@/contexts/AdminContext';
import { useToast } from '@/hooks/use-toast';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ActionButton, OutlineButton } from '@/components/custom-ui/action-button';
import { IconChip } from '@/components/custom-ui/IconChip';
import { NEXILINK_CARD_WHITE_ELEVATED } from '@/lib/nexilink-card-styles';
import { cn } from '@/lib/utils';
import CONFIG from '@/lib/config';

const isValidEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

interface FormState {
    org_name: string;
    org_number: string;
    org_city: string;
    org_address: string;
    org_postal_code: string;
    org_contact_name: string;
    org_contact_email: string;
    org_contact_phone: string;

    unit_name: string;
    unit_city: string;
    unit_description: string;
    unit_contact_name: string;
    unit_contact_email: string;
    unit_contact_phone: string;

    user_email: string;
    user_first_name: string;
    user_last_name: string;
    employer_job_title: string;
}

const EMPTY: FormState = {
    org_name: '', org_number: '', org_city: '', org_address: '', org_postal_code: '',
    org_contact_name: '', org_contact_email: '', org_contact_phone: '',
    unit_name: '', unit_city: '', unit_description: '',
    unit_contact_name: '', unit_contact_email: '', unit_contact_phone: '',
    user_email: '', user_first_name: '', user_last_name: '', employer_job_title: '',
};

const STEPS = [
    { n: 1, icon: Building2, label: 'Organisationen', hint: 'Kunden — kommunen eller företaget.' },
    { n: 2, icon: Building, label: 'Första enheten', hint: 'En avdelning inuti organisationen.' },
    { n: 3, icon: User, label: 'Arbetsgivaren', hint: 'Personen som ansvarar för enheten.' },
] as const;

/** One collapsed group of genuinely-optional fields. */
const Optional: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [open, setOpen] = useState(false);
    return (
        <Collapsible open={open} onOpenChange={setOpen}>
            <CollapsibleTrigger className="flex items-center gap-1.5 text-sm font-medium text-[#1A4D4F] hover:underline pt-1">
                <ChevronDown className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />
                Fler uppgifter (valfritt)
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-4 pt-4">{children}</CollapsibleContent>
        </Collapsible>
    );
};

const NewOrganisationWizard: React.FC = () => {
    const navigate = useNavigate();
    const { createCompleteEmployer, listOrganisations } = useAdmin();
    const { toast } = useToast();

    const [step, setStep] = useState(1);
    const [form, setForm] = useState<FormState>(EMPTY);
    const [submitting, setSubmitting] = useState(false);

    const set = (k: keyof FormState, v: string) => setForm((f) => ({ ...f, [k]: v }));

    // DEV ONLY. Gated on CONFIG.SHOW_DEV_TOOLS, which is derived from APP_ENV and
    // is false in qa and prod (and false for any unrecognised value, so a typo
    // cannot unlock it in a live build). Fills every step at once so the wizard
    // can be exercised end-to-end without typing.
    const fillTestData = () => {
        const suffix = Math.random().toString(36).slice(2, 7);
        setForm({
            ...EMPTY,
            org_name: `Malmö Stad ${suffix}`,
            org_number: '212000-1124',
            org_city: 'Malmö',
            unit_name: `Barn & Unga ${suffix}`,
            unit_city: 'Malmö',
            unit_description: 'Avdelning för barn och ungdomsfrågor',
            user_email: `karin.${suffix}@malmo.test`,
            user_first_name: 'Karin',
            user_last_name: 'Larsson',
            employer_job_title: 'Enhetschef',
        });
        toast({ title: '🧪 Testdata', description: 'Alla tre steg är ifyllda.' });
    };

    // Each step gates on its own single required field, so "Nästa" is never a
    // guess about what is still missing.
    const stepValid =
        step === 1 ? form.org_name.trim().length > 0
        : step === 2 ? form.unit_name.trim().length > 0
        : isValidEmail(form.user_email);

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            const created = await createCompleteEmployer({
                organisation: {
                    name: form.org_name.trim(),
                    org_number: form.org_number.trim() || undefined,
                    city: form.org_city.trim() || undefined,
                    address: form.org_address.trim() || undefined,
                    postal_code: form.org_postal_code.trim() || undefined,
                    contact_name: form.org_contact_name.trim() || undefined,
                    contact_email: form.org_contact_email.trim() || undefined,
                    contact_phone: form.org_contact_phone.trim() || undefined,
                },
                unit: {
                    name: form.unit_name.trim(),
                    city: form.unit_city.trim() || undefined,
                    description: form.unit_description.trim() || undefined,
                    contact_name: form.unit_contact_name.trim() || undefined,
                    contact_email: form.unit_contact_email.trim() || undefined,
                    contact_phone: form.unit_contact_phone.trim() || undefined,
                },
                employer: {
                    // #355 — no password. The backend creates the account without one
                    // and mails a set-password link.
                    email: form.user_email.trim(),
                    first_name: form.user_first_name.trim() || undefined,
                    last_name: form.user_last_name.trim() || undefined,
                    job_title: form.employer_job_title.trim() || undefined,
                },
            });

            if (!created) throw new Error('Kunde inte skapa organisationen.');

            toast({
                title: 'Organisationen är skapad',
                description: `${form.org_name.trim()} finns nu i systemet. ${form.user_email.trim()} har fått ett mejl för att välja lösenord.`,
            });
            await listOrganisations();
            navigate(`/dashboard/organisations/${created.organisation.id}`);
        } catch (err: unknown) {
            toast({
                title: 'Kunde inte skapa organisationen',
                description: (err as Error)?.message || 'Försök igen.',
                variant: 'destructive',
            });
        } finally {
            setSubmitting(false);
        }
    };

    const current = STEPS[step - 1];

    return (
        <div className="max-w-2xl space-y-6">
            {/* Stepper — always visible, so you can see how much is left. */}
            <div className="flex items-center gap-2">
                {STEPS.map((s, i) => (
                    <React.Fragment key={s.n}>
                        <div className="flex items-center gap-2 min-w-0">
                            <span
                                className={cn(
                                    'w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0',
                                    step > s.n ? 'bg-[#1A4D4F] text-white'
                                        : step === s.n ? 'bg-[#A4C3B2]/40 text-[#1A4D4F] ring-2 ring-[#1A4D4F]'
                                        : 'bg-gray-100 text-gray-400',
                                )}
                            >
                                {step > s.n ? <Check className="w-3.5 h-3.5" /> : s.n}
                            </span>
                            <span className={cn('text-sm truncate hidden sm:block', step === s.n ? 'font-semibold text-gray-900' : 'text-gray-400')}>
                                {s.label}
                            </span>
                        </div>
                        {i < STEPS.length - 1 && <div className="flex-1 h-px bg-gray-200" />}
                    </React.Fragment>
                ))}
            </div>

            <div className={NEXILINK_CARD_WHITE_ELEVATED}>
                <div className="p-6 space-y-5">
                    <div className="flex items-center gap-3">
                        <IconChip icon={current.icon} />
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">
                                Steg {step} av 3 — {current.label}
                            </h2>
                            <p className="text-sm text-gray-500">{current.hint}</p>
                        </div>
                    </div>

                    {step === 1 && (
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="org_name">Namn <span className="text-red-500">*</span></Label>
                                <Input
                                    id="org_name"
                                    value={form.org_name}
                                    onChange={(e) => set('org_name', e.target.value)}
                                    placeholder="t.ex. Malmö Stad"
                                    autoFocus
                                />
                            </div>
                            <Optional>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="org_number">Organisationsnummer</Label>
                                        <Input id="org_number" value={form.org_number} onChange={(e) => set('org_number', e.target.value)} placeholder="212000-1124" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="org_city">Stad</Label>
                                        <Input id="org_city" value={form.org_city} onChange={(e) => set('org_city', e.target.value)} placeholder="Malmö" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="org_address">Adress</Label>
                                        <Input id="org_address" value={form.org_address} onChange={(e) => set('org_address', e.target.value)} placeholder="Stortorget 1" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="org_postal_code">Postnummer</Label>
                                        <Input id="org_postal_code" value={form.org_postal_code} onChange={(e) => set('org_postal_code', e.target.value)} placeholder="211 22" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="org_contact_name">Kontaktperson</Label>
                                        <Input id="org_contact_name" value={form.org_contact_name} onChange={(e) => set('org_contact_name', e.target.value)} placeholder="Eva Svensson" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="org_contact_phone">Telefon</Label>
                                        <Input id="org_contact_phone" value={form.org_contact_phone} onChange={(e) => set('org_contact_phone', e.target.value)} placeholder="040-341000" />
                                    </div>
                                    <div className="space-y-2 col-span-2">
                                        <Label htmlFor="org_contact_email">E-post</Label>
                                        <Input id="org_contact_email" type="email" value={form.org_contact_email} onChange={(e) => set('org_contact_email', e.target.value)} placeholder="eva.svensson@malmo.se" />
                                    </div>
                                </div>
                            </Optional>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="unit_name">Namn <span className="text-red-500">*</span></Label>
                                <Input
                                    id="unit_name"
                                    value={form.unit_name}
                                    onChange={(e) => set('unit_name', e.target.value)}
                                    placeholder="t.ex. Barn & Unga"
                                    autoFocus
                                />
                                <p className="text-xs text-gray-500">
                                    Fler enheter lägger du till på {form.org_name.trim() || 'organisationens'} sida efteråt.
                                </p>
                            </div>
                            <Optional>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="unit_city">Stad</Label>
                                        <Input id="unit_city" value={form.unit_city} onChange={(e) => set('unit_city', e.target.value)} placeholder="Malmö" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="unit_contact_name">Kontaktperson</Label>
                                        <Input id="unit_contact_name" value={form.unit_contact_name} onChange={(e) => set('unit_contact_name', e.target.value)} placeholder="Karin Larsson" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="unit_contact_email">E-post</Label>
                                        <Input id="unit_contact_email" type="email" value={form.unit_contact_email} onChange={(e) => set('unit_contact_email', e.target.value)} placeholder="karin.larsson@malmo.se" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="unit_contact_phone">Telefon</Label>
                                        <Input id="unit_contact_phone" value={form.unit_contact_phone} onChange={(e) => set('unit_contact_phone', e.target.value)} placeholder="040-341100" />
                                    </div>
                                    <div className="space-y-2 col-span-2">
                                        <Label htmlFor="unit_description">Beskrivning</Label>
                                        <Textarea id="unit_description" rows={3} value={form.unit_description} onChange={(e) => set('unit_description', e.target.value)} placeholder="Kort beskrivning av enheten…" />
                                    </div>
                                </div>
                            </Optional>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="user_email">E-post <span className="text-red-500">*</span></Label>
                                <Input
                                    id="user_email"
                                    type="email"
                                    value={form.user_email}
                                    onChange={(e) => set('user_email', e.target.value)}
                                    placeholder="t.ex. karin.larsson@malmo.se"
                                    autoFocus
                                />
                                {form.user_email && !isValidEmail(form.user_email) && (
                                    <p className="text-sm text-red-500">Ange en giltig e-postadress</p>
                                )}
                            </div>
                            <div className="rounded-lg bg-[#F3F7F5] border border-[#A4C3B2]/40 p-3 flex gap-2.5">
                                <Mail className="w-4 h-4 text-[#1A4D4F] flex-shrink-0 mt-0.5" />
                                <p className="text-sm text-gray-700">
                                    Arbetsgivaren får ett mejl med en länk för att välja sitt eget lösenord.
                                    Länken gäller i 7 dagar.
                                </p>
                            </div>
                            <Optional>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="user_first_name">Förnamn</Label>
                                        <Input id="user_first_name" value={form.user_first_name} onChange={(e) => set('user_first_name', e.target.value)} placeholder="Karin" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="user_last_name">Efternamn</Label>
                                        <Input id="user_last_name" value={form.user_last_name} onChange={(e) => set('user_last_name', e.target.value)} placeholder="Larsson" />
                                    </div>
                                    <div className="space-y-2 col-span-2">
                                        <Label htmlFor="employer_job_title">Jobbtitel</Label>
                                        <Input id="employer_job_title" value={form.employer_job_title} onChange={(e) => set('employer_job_title', e.target.value)} placeholder="Enhetschef" />
                                    </div>
                                </div>
                            </Optional>

                            {/* Summary — the last screen states what is about to exist, so
                                "Skapa" is never a leap of faith. */}
                            <div className="rounded-lg border border-gray-100 bg-gray-50/60 p-4 space-y-1.5">
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Detta skapas</p>
                                <p className="text-sm text-gray-700"><Building2 className="w-3.5 h-3.5 inline mr-1.5 text-gray-400" />{form.org_name.trim() || '—'}</p>
                                <p className="text-sm text-gray-700"><Building className="w-3.5 h-3.5 inline mr-1.5 text-gray-400" />{form.unit_name.trim() || '—'}</p>
                                <p className="text-sm text-gray-700"><User className="w-3.5 h-3.5 inline mr-1.5 text-gray-400" />{form.user_email.trim() || '—'}</p>
                            </div>
                        </div>
                    )}
                </div>

                <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between gap-3">
                    {CONFIG.SHOW_DEV_TOOLS && (
                        <button
                            type="button"
                            onClick={fillTestData}
                            className="text-xs font-medium px-2.5 py-1.5 rounded-lg bg-yellow-100 hover:bg-yellow-200 text-yellow-800 border border-yellow-300"
                        >
                            🧪 Fyll testdata
                        </button>
                    )}
                    {step > 1 ? (
                        <OutlineButton icon={ArrowLeft} onClick={() => setStep((s) => s - 1)}>Tillbaka</OutlineButton>
                    ) : (
                        <OutlineButton onClick={() => navigate('/dashboard/organisations')}>Avbryt</OutlineButton>
                    )}
                    {step < 3 ? (
                        <ActionButton icon={ArrowRight} disabled={!stepValid} onClick={() => setStep((s) => s + 1)}>
                            Nästa
                        </ActionButton>
                    ) : (
                        <ActionButton icon={Check} disabled={!stepValid} loading={submitting} onClick={handleSubmit}>
                            {submitting ? 'Skapar…' : 'Skapa organisation'}
                        </ActionButton>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NewOrganisationWizard;
